import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Note-type builder API. The `fields[]` / `signatures[]` arrays are
 * JSONB blobs on `note_types`; the server enforces:
 *   - stable `id` on every entry (generated if missing)
 *   - only one `type: 'client'` signature, pinned last in the array
 *   - staff signatures come before the client signature
 * The React builder enforces the same ordering on the way in, but the
 * server is the authority.
 */

// Field types offered by the note-type builder. Legacy `radio` rows are
// still rendered by the NoteForm consumer for backward compat, but the
// builder no longer offers it — single-select does the same job.
export type NoteTypeFieldType =
    | 'text'
    | 'textarea'
    | 'number'
    | 'date'
    | 'checkbox'
    | 'select'
    | 'multiselect'
    | 'heading';

export interface NoteTypeField {
    id: string;
    label: string;
    type: NoteTypeFieldType;
    required?: boolean;   // undefined = required (legacy preservation). `false` = optional.
    helperText?: string;
    fullWidth?: boolean;
    choices?: string[];   // used by select / multiselect
}

export interface NoteTypeSignature {
    id: string;
    label: string;
    type: 'staff' | 'client';
    helperText?: string;
}

export interface NoteType {
    id: string;
    name: string;
    status: 'Active' | 'Inactive';
    fields: NoteTypeField[];
    signatures: NoteTypeSignature[];
}

export interface NoteTypeWritePayload {
    name?: string;
    status?: 'Active' | 'Inactive';
    fields?: NoteTypeField[];
    signatures?: NoteTypeSignature[];
}

export interface NoteTypeUsage {
    sessionTypes: Array<{ id: string; name: string; status: string }>;
}

export interface NoteTypeImpactPreview {
    reorderOnly: boolean;
    droppedFieldIds: string[];
    droppedSigIds: string[];
    addedRequiredFieldIds: string[];
    addedOptionalFieldIds: string[];
    addedSigIds: string[];
    counts: {
        revertedToDrafting: number;    // Complete → Drafting
        submittedRecalled: number;     // Submitted → Drafting (recall)
        advancedToComplete: number;    // Drafting → Complete (field removal closes the gate)
        valuePruned: number;           // values dropped; status unchanged
        unchanged: number;
        finalizedUnaffected: number;
    };
}

function normalizeStatus(s: string): 'Active' | 'Inactive' {
    return String(s).toLowerCase() === 'active' ? 'Active' : 'Inactive';
}

function normalize(row: NoteType & { status: string }): NoteType {
    return {
        ...row,
        status:     normalizeStatus(row.status),
        fields:     Array.isArray(row.fields)     ? row.fields     : [],
        signatures: Array.isArray(row.signatures) ? row.signatures : [],
    };
}

export function useNoteTypes() {
    return useQuery({
        queryKey: ['note-types'] as const,
        queryFn: async () => {
            const res = await api.get<Array<NoteType & { status: string }>>('/note-types');
            return res.data.map(normalize);
        },
        staleTime: 60_000,
    });
}

export function useNoteType(id: string | undefined) {
    return useQuery({
        queryKey: ['note-type', id] as const,
        queryFn: async () => {
            const res = await api.get<NoteType & { status: string }>(`/note-types/${id}`);
            return normalize(res.data);
        },
        enabled: !!id,
        staleTime: 60_000,
    });
}

export function useNoteTypeUsage(id: string | undefined) {
    return useQuery({
        queryKey: ['note-type-usage', id] as const,
        queryFn: async () => {
            const res = await api.get<NoteTypeUsage>(`/note-types/${id}/usage`);
            return res.data;
        },
        enabled: !!id,
        staleTime: 60_000,
    });
}

export function useCreateNoteType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: NoteTypeWritePayload) => {
            const res = await api.post<NoteType & { status: string }>('/note-types', payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['note-types'] }),
    });
}

export function useUpdateNoteType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: NoteTypeWritePayload }) => {
            const res = await api.patch<NoteType & { status: string }>(`/note-types/${id}`, payload);
            return normalize(res.data);
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['note-types'] });
            qc.invalidateQueries({ queryKey: ['note-type', vars.id] });
            // A cascade may have rewritten session.note_status + note_data on
            // Drafting / Complete / Submitted notes of this type.
            qc.invalidateQueries({ queryKey: ['session-detail'] });
            qc.invalidateQueries({ queryKey: ['sessions'] });
        },
    });
}

export function useDeleteNoteType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/note-types/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['note-types'] }),
    });
}

export function useDuplicateNoteType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, name }: { id: string; name?: string }) => {
            const res = await api.post<NoteType & { status: string }>(
                `/note-types/${id}/duplicate`,
                name ? { name } : {},
            );
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['note-types'] }),
    });
}

/** Dry-run cascade against a candidate payload — no writes, just counts. */
export async function fetchImpactPreview(
    id: string,
    payload: Pick<NoteTypeWritePayload, 'fields' | 'signatures'>,
): Promise<NoteTypeImpactPreview> {
    const res = await api.post<NoteTypeImpactPreview>(`/note-types/${id}/impact-preview`, payload);
    return res.data;
}
